import React, { useEffect, useMemo, useState, useRef } from 'react'
import { Link, useParams } from 'react-router-dom';
import Slider from 'react-slick';
import { useProduct } from '../hooks/product';
import { useCurrenciesStore, useMoney } from '../stores/currenciesStore';
import { useTranslation } from 'react-i18next';
import useCartStore, { useCart } from '../stores/cartStore';
import { useSettingsStore } from '../stores/settingsStore'
import SocialLinks from './SocialLinks'
import ProductCard from './ProductCard'
import axiosInstance from '../utils/axiosInstance';
import LoadingSpinner from './LoadingSpinner';

// Custom CSS for product thumbnails slider arrows
const thumbnailSliderStyles = {
    arrowContainer: {
        display: 'flex',
        gap: '8px'
    }
};

const ProductDetailsTwo = () => {

    const {t} = useTranslation()
    const {productSlug} = useParams()
    const {product, loading} = useProduct(productSlug)
    const {selectedCurrency} = useCurrenciesStore((state) => state)
    const {money} = useMoney()
    const [quantity, setQuantity] = useState(1);
    const { addToCart } = useCart();
    const settings = useSettingsStore((state) => state.settings)
    const incrementQuantity = () => setQuantity(quantity + 1);
    const decrementQuantity = () => setQuantity(quantity > 1 ? quantity - 1 : quantity);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [loadingRelated, setLoadingRelated] = useState(false);
    const sliderRef = useRef(null);
    const thumbnailSliderRef = useRef(null);

    const productPrice = useMemo(() => {
        if (product?.computed_price === undefined || product?.computed_price === null) {
            return money(0);
        }
        return money(product.computed_price);
    }, [product?.computed_price, money]);
    
    const regularPrice = useMemo(() => {
        if (product?.price === undefined || product?.price === null) {
            return money(0);
        }
        return money(product.price);
    }, [product?.price, money]);
    
    const samePrice = useMemo(() => {
        if (!product?.price || !product?.computed_price) return true;
        return product.price === product.computed_price;
    }, [product?.price, product?.computed_price]);
    
    const discountPercentage = useMemo(() => {
        if (!product?.price || !product?.computed_price || product.price <= 0 || product.price <= product.computed_price) {
            return 0;
        }
        return Math.round(((product.price - product.computed_price) / product.price) * 100);
    }, [product?.price, product?.computed_price]);

    const subtotal = useMemo(() => {
        if (!product?.computed_price) {
            return 0;
        }
        return product.computed_price * quantity;
    }, [quantity, product]);

    const subtotalFormatted = useMemo(() => {
        return money(subtotal || 0);
    }, [subtotal, money]);

    const [mainImage, setMainImage] = useState(null);

    useEffect(() => {
        if (product?.images?.length > 0 && !mainImage) {
            setMainImage(product.images[0]);
        }
    }, [product]);

    // Fetch related products
    useEffect(() => {
        if (product?.id) {
            setLoadingRelated(true);
            axiosInstance
                .get(`/api/products-for-store/${product.id}/related`)
                .then((res) => {
                    setRelatedProducts(res.data.data || []);
                })
                .catch((err) => {
                    console.error('Error fetching related products:', err);
                })
                .finally(() => {
                    setLoadingRelated(false);
                });
        }
    }, [product]);

    const settingsThumbs = {
        dots: false,
        infinite: false,
        speed: 500,
        slidesToShow: 4,
        slidesToScroll: 1,
        focusOnSelect: false,
        arrows: false,
        draggable: false
    };

    // Navigation functions for the thumbnail slider
    const goToPrevThumbnail = () => {
        if (thumbnailSliderRef.current) {
            thumbnailSliderRef.current.slickPrev();
        }
    };

    const goToNextThumbnail = () => {
        if (thumbnailSliderRef.current) {
            thumbnailSliderRef.current.slickNext();
        }
    };

    function SampleNextArrow() {
        return (
            <div style={thumbnailSliderStyles.arrowContainer}>
                <button
                    type="button" 
                    onClick={goToPrevThumbnail}
                    className="w-32 h-32 text-sm border border-gray-100 flex-center rounded-circle hover-border-main-600 hover-bg-main-600 hover-text-white transition-1"
                >
                    <i className="ph ph-caret-left" />
                </button>
                <button
                    type="button" 
                    onClick={goToNextThumbnail}
                    className="w-32 h-32 text-sm border border-gray-100 flex-center rounded-circle hover-border-main-600 hover-bg-main-600 hover-text-white transition-1"
                >
                    <i className="ph ph-caret-right" />
                </button>
            </div>
        );
    }

    function SamplePrevArrow() {
        // We'll use only the NextArrow component since we're combining both arrows in one container
        return null;
    }

    const relatedProductsSettings = {
        dots: false,
        arrows: false,
        infinite: relatedProducts.length > 4,
        speed: 1000,
        slidesToShow: Math.min(5, relatedProducts.length || 1),
        slidesToScroll: 1,
        initialSlide: 0,
        autoplay: relatedProducts.length > 0,
        responsive: [
            {
                breakpoint: 1399,
                settings: {
                    slidesToShow: Math.min(3, relatedProducts.length || 1),
                },
            },
            {
                breakpoint: 1199,
                settings: {
                    slidesToShow: Math.min(2, relatedProducts.length || 1),
                },
            },
            {
                breakpoint: 575,
                settings: {
                    slidesToShow: 1,
                },
            },
        ],
    };

    // Navigation functions for the slider
    const goToPrevSlide = () => {
        if (sliderRef.current) {
            sliderRef.current.slickPrev();
        }
    };

    const goToNextSlide = () => {
        if (sliderRef.current) {
            sliderRef.current.slickNext();
        }
    };

    const handleAddToCart = () => {
        if (product) {
            addToCart(product, quantity);
        }
    };

    const handleBuyNow = () => {
        if (product) {
            addToCart(product, quantity);
            // Navigate to cart page
            window.location.href = '/cart';
        }
    };

    // Loading placeholders
    const renderPlaceholder = (width = "100%", height = "24px", className = "") => (
        <div 
            className={`bg-gray-100 rounded-8 animate-pulse ${className}`} 
            style={{ width, height }}
        ></div>
    );

    return (
        <section className="product-details py-80">
            <div className="container container-lg">
                <div className="row gy-4">
                    <div className="col-xl-9">
                        <div className="row gy-4">
                            <div className="col-xl-6">
                                <div className="product-details__left">
                                    <div className="border border-gray-100 product-details__thumb-slider !tw-max-h-fit rounded-16">
                                        <div className="product-details__thumb flex-center tw-w-full tw-h-full">
                                            {loading ? (
                                                <div className="tw-h-full tw-w-full tw-flex tw-items-center tw-justify-center bg-gray-50 animate-pulse">
                                                    <LoadingSpinner width={48} height={48} />
                                                </div>
                                            ) : (
                                                <img src={mainImage} alt="Main Product" className='tw-object-contain tw-h-full' />
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-24">
                                        <div className="product-details__images-slider tw-relative tw-flex tw-justify-between">
                                            {loading ? (
                                                <div className="tw-flex tw-gap-4 tw-overflow-hidden">
                                                    {[1, 2, 3, 4].map((i) => (
                                                        <div key={i} className="p-8 border border-gray-100 center max-w-120 max-h-120 h-100 flex-center rounded-16 bg-gray-50 animate-pulse"></div>
                                                    ))}
                                                </div>
                                            ) : product?.images?.length > 0 ? (
                                                <Slider ref={thumbnailSliderRef} {...settingsThumbs} className="tw-w-full">
                                                    {product.images.map((image, index) => (
                                                        <div className="p-8 border border-gray-100 center max-w-120 max-h-120 h-100 flex-center rounded-16" key={index} onClick={() => setMainImage(image)}>
                                                            <img className='thum' src={image} alt={`Thumbnail ${index}`} />
                                                        </div>
                                                    ))}
                                                </Slider>
                                            ) : null}
                                            <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
                                                <SampleNextArrow />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-6">
                                <div className="product-details__content">
                                    {loading ? (
                                        <>
                                            {renderPlaceholder("70%", "24px", "mb-12")}
                                            <div className="flex-wrap gap-12 !tw-hidden lg:tw-flex">
                                                {renderPlaceholder("60px", "20px")}
                                            </div>
                                            <span className="pt-32 mt-32 text-gray-700 border-gray-100 border-top d-block" />
                                            {renderPlaceholder("90%", "80px", "text-gray-700")}
                                            <div className="flex-wrap gap-16 my-32 flex-align">
                                                <div className="gap-8 flex-align">
                                                    {renderPlaceholder("150px", "28px")}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <h5 className="mb-12">
                                                {product?.name}
                                            </h5>
                                            <div className="flex-wrap gap-12 !tw-hidden lg:tw-flex">
                                                <span className="text-gray-900">
                                                    {" "}
                                                </span>
                                            </div>
                                            <span className="pt-32 mt-32 text-gray-700 border-gray-100 border-top d-block" />
                                            <p className="text-gray-700">
                                                {product?.short_description}
                                            </p>
                                            <div className="flex-wrap gap-16 my-32 flex-align">
                                                <div className="gap-8 flex-align">
                                                    {!samePrice && discountPercentage > 0 && (
                                                        <div className="gap-8 flex-align text-main-two-600">
                                                            <i className="text-xl ph-fill ph-seal-percent" />
                                                            -{discountPercentage}%
                                                        </div>
                                                    )}
                                                    {!samePrice && (
                                                        <h6 className="mb-0 mr-2 text-xl text-gray-400 fw-medium text-decoration-line-through">
                                                            {regularPrice}
                                                        </h6>
                                                    )}
                                                    <h6 className="mb-0">{productPrice}</h6>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    <span className="pt-32 mt-32 text-gray-700 border-gray-100 border-top d-block" />
                                    <Link
                                        to={`https://wa.me/${settings.store_phone?.replace(/\D/g, '')}?text=${encodeURIComponent(t('product.whatsapp_message').replace('{productName}', product?.name || ''))}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="gap-8 py-16 btn btn-black flex-center rounded-8"
                                    >
                                        <i className="text-lg ph ph-whatsapp-logo" />
                                        {t('product.request_more_information')}
                                    </Link>
                                    <div className="mt-32">
                                        <span className="text-gray-900 fw-medium">
                                            100% {t('product.safe_checkout')}
                                        </span>
                                        <div className="mt-10">
                                            <img src="assets/images/thumbs/gateway-img.png" alt="" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3">
                        <div className="px-32 py-40 border border-gray-100 product-details__sidebar rounded-16">
                            <label
                                htmlFor="delivery"
                                className="mb-8 h6 activePage text-heading fw-semibold d-block"
                            >
                                {t('product.buy')}
                            </label>
                            <div className="mb-32">
                                <span className="text-xl d-flex">
                                    <i className="ph ph-location" />
                                </span>
                                <div className="overflow-hidden d-flex rounded-4">
                                    <button onClick={decrementQuantity}
                                        type="button"
                                        disabled={loading}
                                        className="flex-shrink-0 w-48 h-48 quantity__minus text-neutral-600 bg-gray-50 flex-center hover-bg-main-600 hover-text-white"
                                    >
                                        <i className="ph ph-minus" />
                                    </button>
                                    <input
                                        type="number"
                                        className="w-32 px-16 text-center border border-gray-100 quantity__input flex-grow-1 border-start-0 border-end-0"
                                        id="stock"
                                        value={
                                            quantity
                                        } readOnly

                                    />
                                    <button onClick={incrementQuantity}
                                        type="button"
                                        disabled={loading}
                                        className="flex-shrink-0 w-48 h-48 quantity__plus text-neutral-600 bg-gray-50 flex-center hover-bg-main-600 hover-text-white"
                                    >
                                        <i className="ph ph-plus" />
                                    </button>
                                </div>
                            </div>
                            <div className="mb-32">
                                <div className="flex-wrap gap-8 pb-16 mb-16 border-gray-100 flex-between border-bottom">
                                    <span className="text-gray-500">Subtotal</span>
                                    {loading ? (
                                        renderPlaceholder("80px", "24px", "mb-0 text-lg")
                                    ) : (
                                        <h6 className="mb-0 text-lg">{subtotalFormatted}</h6>
                                    )}
                                </div>
                            </div>
                            <Link
                                to="#"
                                onClick={handleAddToCart}
                                className={`gap-8 py-16 mt-48 btn ${loading ? 'btn-secondary' : 'btn-main'} flex-center rounded-8 fw-normal ${loading ? 'disabled' : ''}`}
                                disabled={loading}
                            >
                                <i className="text-lg ph ph-shopping-cart-simple" />
                                {t('product.add_to_cart')}
                            </Link>
                            <Link
                                to="#"
                                onClick={handleBuyNow}
                                className={`py-16 mt-16 btn ${loading ? 'btn-outline-secondary' : 'btn-outline-main'} rounded-8 fw-normal w-100 ${loading ? 'disabled' : ''}`}
                                disabled={loading}
                            >
                                {t('product.buy_now')}
                            </Link>
                        </div>
                    </div>
                </div>
                <div className="pt-80">
                    <div className="border product-dContent rounded-24">
                        <div className="flex-wrap gap-16 border-gray-100 product-dContent__header border-bottom flex-between">
                            <ul
                                className="mb-3 nav common-tab nav-pills"
                                id="pills-tab"
                                role="tablist"
                            >
                                <li className="nav-item" role="presentation">
                                    <button
                                        className="nav-link active"
                                        id="pills-description-tab"
                                        data-bs-toggle="pill"
                                        data-bs-target="#pills-description"
                                        type="button"
                                        role="tab"
                                        aria-controls="pills-description"
                                        aria-selected="true"
                                    >
                                        {t('product.description')}
                                    </button>
                                </li>
                            </ul>
                            <Link
                                to="#"
                                className="gap-8 btn bg-color-one rounded-16 flex-align text-main-600 hover-bg-main-600 hover-text-white"
                            >
                                <img src="assets/images/icon/satisfaction-icon.png" alt="" />
                                100% {t('product.satisfaction_guaranteed')}
                            </Link>
                        </div>
                        <div className="product-dContent__box">
                            <div className="tab-content" id="pills-tabContent">
                                <div
                                    className="tab-pane fade show active"
                                    id="pills-description"
                                    role="tabpanel"
                                    aria-labelledby="pills-description-tab"
                                    tabIndex={0}
                                >
                                    <div className="mb-40">
                                        <h6 className="mb-24">{t('product.description')}</h6>
                                        {loading ? (
                                            <div className="space-y-4">
                                                {renderPlaceholder("100%", "16px")}
                                                {renderPlaceholder("90%", "16px")}
                                                {renderPlaceholder("95%", "16px")}
                                                {renderPlaceholder("80%", "16px")}
                                            </div>
                                        ) : (
                                            product?.full_description
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Related Products Carousel */}
                {relatedProducts.length > 0 && (
                    <div className="pt-80">
                        <div className="mb-40 position-relative">
                            <div className="flex-wrap gap-8 flex-between section-title">
                                <h3 className="mb-0">{t('product.related_products')}</h3>
                                <div className="gap-8 flex-align">
                                    <button
                                        type="button"
                                        onClick={goToPrevSlide}
                                        className="w-48 h-48 text-xl border border-gray-100 flex-center rounded-circle hover-border-main-600 hover-bg-main-600 hover-text-white transition-1"
                                    >
                                        <i className="ph ph-caret-left" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={goToNextSlide}
                                        className="w-48 h-48 text-xl border border-gray-100 flex-center rounded-circle hover-border-main-600 hover-bg-main-600 hover-text-white transition-1"
                                    >
                                        <i className="ph ph-caret-right" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="position-relative">
                            {loadingRelated ? (
                                <div className="gap-16 py-40 flex-center flex-column">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">{t('common.loading')}</span>
                                    </div>
                                    <p className="text-gray-500">{t('common.loading')}</p>
                                </div>
                            ) : (
                                <Slider ref={sliderRef} {...relatedProductsSettings}>
                                    {relatedProducts.map((product) => (
                                        <div key={product.slug} className="px-8">
                                            <ProductCard 
                                                product={{ 
                                                    id: product.id,
                                                    slug: product.slug,
                                                    name: product.name,
                                                    price: product.price,
                                                    computed_price: product.computed_price,
                                                    images: product.images
                                                }}
                                            />
                                        </div>
                                    ))}
                                </Slider>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </section>
    )
}

export default ProductDetailsTwo